import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, Check } from "lucide-react";
import { formatDateTime } from "@/lib/utils";
import { cn } from "@/lib/utils";
import { useAuth } from "@/features/auth/auth-context";
import {
  listNotifications,
  markAllRead,
  markRead,
  type AppNotification,
} from "./api";
import { useNotificationSocket } from "./useNotificationSocket";

export function NotificationBell() {
  const { isAuthenticated } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // Live pushes come over the socket; the poll is a safety net for a dropped
  // connection, not the primary path — hence the generous interval.
  useNotificationSocket(isAuthenticated);

  const { data } = useQuery({
    queryKey: ["notifications", "list"],
    queryFn: () => listNotifications(1),
    enabled: isAuthenticated,
    refetchInterval: 120_000,
  });

  const read = useMutation({
    mutationFn: markRead,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
  const readAll = useMutation({
    mutationFn: markAllRead,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  if (!isAuthenticated) return null;

  const unread = data?.unread_count ?? 0;
  const items = data?.items ?? [];

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={`Notifications${unread > 0 ? `, ${unread} unread` : ""}`}
        className="relative rounded-md border border-border p-2 hover:bg-accent"
      >
        <Bell className="h-4 w-4" aria-hidden="true" />
        {unread > 0 && (
          <span
            className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold text-destructive-foreground"
            aria-hidden="true"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-80 rounded-lg border border-border bg-card text-card-foreground shadow-lg">
          <div className="flex items-center justify-between border-b border-border px-3 py-2">
            <span className="text-sm font-medium">Notifications</span>
            {unread > 0 && (
              <button
                type="button"
                onClick={() => readAll.mutate()}
                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
              >
                <Check className="h-3 w-3" aria-hidden="true" />
                Mark all read
              </button>
            )}
          </div>

          <ul className="max-h-96 overflow-y-auto">
            {items.length === 0 && (
              <li className="px-3 py-8 text-center text-sm text-muted-foreground">
                You're all caught up.
              </li>
            )}
            {items.map((n) => (
              <NotificationRow key={n.id} notification={n} onRead={() => read.mutate(n.id)} />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function NotificationRow({
  notification,
  onRead,
}: {
  notification: AppNotification;
  onRead: () => void;
}) {
  return (
    <li
      className={cn(
        "border-b border-border px-3 py-2.5 last:border-b-0",
        !notification.is_read && "bg-primary/5",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-medium">{notification.title}</div>
          {notification.body && (
            <div className="truncate text-xs text-muted-foreground">{notification.body}</div>
          )}
          <div className="mt-0.5 text-[11px] text-muted-foreground">
            {formatDateTime(notification.created_at)}
          </div>
        </div>
        {!notification.is_read && (
          <button
            type="button"
            onClick={onRead}
            aria-label="Mark read"
            className="mt-0.5 shrink-0 rounded p-1 hover:bg-accent"
          >
            <Check className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        )}
      </div>
    </li>
  );
}
